import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { MessageCircle, Trash2, CheckCircle, Clock } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminFeedback = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [feedbacks, setFeedbacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    if (user?.role !== 'admin') {
      navigate('/create');
      return;
    }
    fetchFeedbacks();
  }, [user, navigate, statusFilter]);

  const fetchFeedbacks = async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      
      const response = await axios.get(`${API_URL}/api/feedback/admin/list`, { params });
      setFeedbacks(response.data);
    } catch (error) {
      toast.error('獲取反饋失敗');
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (feedbackId, newStatus) => {
    try {
      await axios.put(`${API_URL}/api/feedback/admin/${feedbackId}/status`, {
        status: newStatus
      });
      toast.success('狀態已更新');
      fetchFeedbacks();
    } catch (error) {
      toast.error('更新失敗');
    }
  };

  const deleteFeedback = async (feedbackId) => {
    if (!window.confirm('確定要刪除此反饋嗎？')) return;
    
    try {
      await axios.delete(`${API_URL}/api/feedback/admin/${feedbackId}`);
      toast.success('已刪除');
      fetchFeedbacks();
    } catch (error) {
      toast.error('刪除失敗');
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: 'bg-yellow-100 text-yellow-700',
      reviewed: 'bg-blue-100 text-blue-700',
      resolved: 'bg-green-100 text-green-700'
    };
    const labels = {
      pending: '待處理',
      reviewed: '已查看',
      resolved: '已解決'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${badges[status]}`}>
        {labels[status]}
      </span>
    );
  };

  if (user?.role !== 'admin') return null;

  return (
    <>
      <header className="bg-white border-b border-neutral-200 px-8 py-6 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-heading font-bold text-neutral-900">用戶反饋</h1>
            <p className="text-neutral-600 text-sm mt-1">查看和管理用戶反饋意見</p>
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="bg-white w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="all" className="bg-white hover:bg-primary-50">全部</SelectItem>
              <SelectItem value="pending" className="bg-white hover:bg-yellow-50">待處理</SelectItem>
              <SelectItem value="reviewed" className="bg-white hover:bg-blue-50">已查看</SelectItem>
              <SelectItem value="resolved" className="bg-white hover:bg-green-50">已解決</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </header>

      <main className="flex-1 overflow-y-auto p-8">
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-neutral-600">載入中...</p>
          </div>
        ) : feedbacks.length === 0 ? (
          <Card className="p-12 text-center">
            <MessageCircle className="w-16 h-16 text-neutral-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-neutral-900 mb-2">尚無用戶反饋</h3>
            <p className="text-neutral-600">用戶提交反饋後會顯示在此</p>
          </Card>
        ) : (
          <div className="space-y-4">
            {feedbacks.map((feedback) => (
              <Card key={feedback.id} className="p-6">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                      <MessageCircle className="w-5 h-5 text-primary-600" />
                    </div>
                    <div>
                      <p className="font-medium text-neutral-900">{feedback.user_name || feedback.user_email}</p>
                      <p className="text-sm text-neutral-500">{feedback.user_email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {getStatusBadge(feedback.status)}
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => deleteFeedback(feedback.id)}
                      className="text-red-600 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                <p className="text-neutral-700 mb-3 whitespace-pre-wrap">{feedback.content}</p>
                
                <div className="flex items-center justify-between pt-3 border-t">
                  <p className="text-xs text-neutral-500">
                    {new Date(feedback.created_at).toLocaleString('zh-TW')}
                  </p>
                  <div className="flex gap-2">
                    {feedback.status !== 'reviewed' && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => updateStatus(feedback.id, 'reviewed')}
                        className="rounded-lg"
                      >
                        <Clock className="w-3 h-3 mr-1" />
                        標記已查看
                      </Button>
                    )}
                    {feedback.status !== 'resolved' && (
                      <Button
                        size="sm"
                        onClick={() => updateStatus(feedback.id, 'resolved')}
                        className="rounded-lg"
                      >
                        <CheckCircle className="w-3 h-3 mr-1" />
                        標記已解決
                      </Button>
                    )}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </main>
    </>
  );
};

export default AdminFeedback;
